/**
 * Common utilities used throughout the app
 */
export const satsToBtc = (sats) => sats / 100000000;

export const btcToString = (x) =>
  x.toLocaleString(undefined, {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });

export const fmtUsd = (x) =>
  x
    ? x.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : 0;

export const truncateHash = (s) =>
  `${s.substring(0, 6)}...${s.substring(s.length - 6)}`;

const isNumber = (x) => typeof x === "number" && Number.isFinite(x);

// Volumes and market caps, which on the watchlist run from under a billion to
// over a trillion. Three significant figures in compact notation - $59.6B,
// $1.58T, $777M - so every row reads the same way.
//
// This replaced a hand-rolled formatter that gave three different shapes in one
// column: "38.7840 B" with four decimals and no dollar sign, "$922 M" with one,
// and "1557.9931 B" where a trillion should have been.
const compactUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumSignificantDigits: 3,
});

export const fmtCompactUsd = (x) => (isNumber(x) ? compactUsd.format(x) : "—");

// A BTC amount in a table, where the column is being read down and compared.
//
// Two decimals, not btcToString's eight: that one exists for a single
// transaction, where the satoshis are the point. A custody table running from
// 1,713 BTC to 116,511 BTC does not need them, and eight decimals across a
// column is a wall of zeros that hides the digits that differ.
//
// Not rounded to whole coins either, for the reason the alert bot gives: large
// amounts cluster, and two neighbouring rows reading the same number look like
// a bug whatever the truth is.
export const fmtBtc = (x) =>
  isNumber(x)
    ? x.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

// The same amount as a headline, where the shape of the number matters more
// than its digits - "155.3K" reads at a glance in a way "155,267.22" does not.
const compactBtc = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumSignificantDigits: 4,
});

export const fmtCompactBtc = (x) => (isNumber(x) ? compactBtc.format(x) : "—");

// A coin's price. Two decimals above a dollar, as money is usually written, but
// significant figures below it: at two decimals DOGE read $0.08 and ENA $0.14,
// which throws away most of the number for any coin worth less than a dollar.
export const fmtPrice = (x) => {
  if (!isNumber(x)) return "—";
  if (x >= 1) {
    return x.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  // A minimum as well as a maximum: a stablecoin at 0.99998 otherwise rounds to
  // a bare "$1" beside neighbours reading "$0.9998", as if it were a different
  // kind of number.
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumSignificantDigits: 4,
    maximumSignificantDigits: 4,
  });
};

export const fmtChange = (x) => {
  // Deliberately not `if (!x)`: a change of exactly 0 is a real value and has to
  // render like any other. CoinGecko returns one for the 1h column on about a
  // third of rows.
  if (!isNumber(x)) {
    return "—";
  }

  // Always two decimals and an explicit sign, so a column of changes lines up
  // and +0.50% cannot be misread as a loss at a glance.
  const fixed = Math.abs(x).toFixed(2);
  if (Number(fixed) === 0) return "0.00%";
  return `${x > 0 ? "+" : "−"}${fixed}%`;
};
